import React, { useState, useCallback } from "react";
import {
	Modal,
	Button
} from "rhombus-react";

import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { reset } from "redux-form";
import { useFlag } from '@innovid/feature-flags-react';

import useActiveCreative from "hooks/useActiveCreative";
import useCreativeDetail from "hooks/useCreativeDetail";
import { useReplayPreviewOnRenderNotify } from "hooks/useReplayPreviewOnRenderNotify";

import { isAfterEffectsCreative, isImposiumCreative, safeDecodeURIComponent } from "utils/common";
import { isYoutubeVAST } from "utils/CreativeActions";

import ProgressContainer from "widgets/ProgressContainer";
import CreativePreviewBladeToolbar from "widgets/modals/creatives/preview/CreativePreviewBladeToolbar";
import { PreviewCreative } from "utils/Preview";
import { LABEL_MANAGEMENT } from "widgets/forms/list";
import LabelsPanel from "widgets/modals/creatives/preview/LabelsPanel";
import PredictiveScorePanel from "widgets/modals/creatives/preview/PredictiveScorePanel";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";
import useQueryParams from "hooks/useQueryParams";
import { FEATURE_FLAGS } from 'featureFlag/featureFlag';

import CreativePreview from "./CreativePreview";
import YoutubePreview from "./YoutubePreview";

import "./CreativePreviewBlade.css";

export default function CreativePreviewBlade(props){
	let { returnPath, returnState, libraryId, creativeId } = props
		,  dispatch = useDispatch()
		, [isFullScreen, setIsFullScreen] = useState(false)
		, [isPreviewLoaded, setIsPreviewLoaded] = useState(false)
		, [randomKey, setRandomKey] = useState(0)
		;

	const forceNewPreview = useFlag(FEATURE_FLAGS.NEW_PREVIEW_LINKS);
	const isPredictiveScoringOn = usePredictiveScoringEnabled();
	const { getParam, setParam } = useQueryParams();
	// Panel is hidden by default; shown only when the toolbar's predictive-score icon sets showScore=true.
	const showScore = getParam("showScore") === "true";
	const creative = useSelector(state => state.creativeDetails);
	const {
		filename,
		width,
		height,
		adType,
		origin,
		isLoaded,
		youtubeUrl,
	} = creative;
	const title = filename ? safeDecodeURIComponent(filename) : " ";

	useActiveCreative(creativeId);

	creativeId = parseInt(creativeId);
	const previewURL = PreviewCreative.private(creative, forceNewPreview).url;

	useCreativeDetail(creativeId);

	const onReplay = useCallback(() => {
		setRandomKey(previousKey => previousKey + 1);
	}, []);

	useReplayPreviewOnRenderNotify({
		creativeId,
		onReplay,
		enabled: isImposiumCreative(creative) || isAfterEffectsCreative(creative),
	});

	function closeModal(){
		dispatch(reset(LABEL_MANAGEMENT)); /*
			We don't destroy the label management form on unmount, so we have to reset it manually
			when the blade closes.
		*/
		dispatch(push({
			pathname: returnPath,
			state: returnState,
		}));
	}

	function onToggleFullScreen(){
		setIsFullScreen(!isFullScreen);
	}

	function Footer(){
		return [
			<Button
				key="close"
				theme="default"
				onClick={closeModal}
				className="close-btn"
			>
				Close
			</Button>,
		];
	}

	function Content(){
		let onPreviewLoad = () => { setIsPreviewLoaded(true); };
		let $preview;

		if(isYoutubeVAST(adType)){
			$preview = (
				<YoutubePreview
					key={randomKey}
					youtubeUrl={youtubeUrl}
					onLoad={onPreviewLoad}
				/>
			);
		} else {
			$preview = (
				<CreativePreview
					key={randomKey}
					keyId={randomKey}	//We set a key here in order for the `replay()` functionality to work and force a re-render
					width={width}
					height={height}
					creativeId={creativeId}
					adType={adType}
					origin={origin}
					onLoad={onPreviewLoad}
				/>
			);
		}

		return (
			<div className="main-content-container">
				<ProgressContainer isLoading={!isLoaded || !isPreviewLoaded}>
					{$preview}
				</ProgressContainer>
				<LabelsPanel
					libraryId={libraryId}
					creativeId={creativeId}
				/>
				{isPredictiveScoringOn && showScore && (
					<PredictiveScorePanel
						entityType="CREATIVE"
						entityId={creativeId}
						libraryId={libraryId}
						onClose={() => setParam("showScore", "false", true)}
					/>
				)}
				<CreativePreviewBladeToolbar
					isFullScreen
					onReplay={onReplay}
					previewURL={previewURL}
				/>
			</div>
		);
	}

	let modalProps = {
		title,
		type: "blade",
		className: "creative-preview-blade expandable",
		setAppTitle:false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,

		renderFooter: Footer,
		renderContent: Content,

		onClose: closeModal,
	};

	return <Modal {...modalProps} />;
}

CreativePreviewBlade.defaultProps = {
	returnPath:"",
	returnState: {},
	libraryId:-1,
	creativeId:-1,
};
